import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Separator,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    Panel,
    PanelType,
    Checkbox,
    Dropdown,
    IDropdownOption,
    TagPicker,
    ITag,
    ComboBox,
    IComboBoxOption,
    SpinButton,
    Icon,
    TooltipHost,
    DirectionalHint
} from '@fluentui/react';
import {
    AddRegular,
    DeleteRegular,
    EditRegular,
    DatabaseRegular,
    SettingsRegular,
    CheckmarkCircleRegular,
    WarningRegular,
    SearchRegular,
    TagRegular,
    ShareRegular,
    MailRegular,
    CalendarRegular,
    FolderRegular,
    CloudRegular,
    InfoRegular,
    TableRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import { TagInput } from '../common/TagInput';

// Types for our tile-based data source configuration
interface DataSourceTile {
    id: string;
    name: string;
    description: string;
    icon: string;
    connector_type: string;
    keywords_en: string[];
    keywords_he: string[];
    azure_indexes: string[];
    web_urls: string[];  // For real-time web search configuration
    agent_id?: string;   // Azure AI Foundry Agent ID for custom Bing search grounding
    // Azure Blob Storage fields
    blob_container?: string;
    blob_prefix?: string;
    // Document Translation fields
    translation_output_container?: string;
    translation_default_language?: string;
    confidence_threshold: number;
    priority: number;
    enabled: boolean;
    use_smart_search: boolean;
    fallback_enabled: boolean;
    source_privacy: 'public' | 'private';  // Privacy classification for FAQ caching
    examples: string[];
    is_default: boolean;
    is_grounding_source: boolean;
    enable_acl_filtering: boolean;
    // Data Source Access Permissions
    access_mode: 'all_users' | 'specific_users';
    allowed_users: string[];
    allowed_groups: string[];
    grounding_system_message?: string;
    grounding_no_results_message?: string;
    // Agentic retrieval fields
    use_agentic_retrieval: boolean;
    agentic_knowledge_base?: string;      // The actual KB name (e.g., "myproject-kb")
    agentic_knowledge_source?: string;    // The actual source name (e.g., "myproject-source")
    agentic_kb_description?: string;  // Description for the knowledge base
    agentic_user_description?: string;
    agentic_retrieval_instructions?: string;
    agentic_answer_instructions?: string;
    agentic_output_mode?: string;
    agentic_max_tokens?: number;
    agentic_max_output_size?: number;  // Max output size for agentic retrieval (default: 5000)
    agentic_reasoning_effort?: string;  // Reasoning effort level (low, medium, high, or minimal)
    agentic_temperature?: number;
    // Structured data source fields
    structured_source_id?: string;  // ID of the structured data source (Excel/SQL)
    structured_source_name?: string;  // Display name of the structured source
    created_at?: string;
    updated_at?: string;
}

// Structured Data Source interface
interface StructuredSource {
    id: string;
    name: string;
    description?: string;
    source_type: string;
    is_active: boolean;
    tables_count?: number;
}

interface SearchIndex {
    name: string;
    description: string;
    keywords: string[];
    enabled?: boolean;
    priority?: number;
    document_count?: number;
    is_configured?: boolean;
}

// Agent interface for AI Foundry agents
interface Agent {
    id: string;
    name: string;
    instructions?: string;
    description?: string;
    model?: string;
    tools_configured?: boolean;
    grounding_type?: string;
    custom_search_config_name?: string;
}

const stackTokens: IStackTokens = { childrenGap: 15 };

// Info tooltip component for field descriptions
interface FieldInfoProps {
    title: string;
    description: string;
}

const FieldInfo: React.FC<FieldInfoProps> = ({ title, description }) => (
    <TooltipHost
        content={
            <div style={{ padding: '8px', maxWidth: '300px' }}>
                <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, display: 'block', marginBottom: '4px' }}>
                    {title}
                </Text>
                <Text variant="small" style={{ color: '#666' }}>
                    {description}
                </Text>
            </div>
        }
        directionalHint={DirectionalHint.topCenter}
        styles={{ root: { display: 'inline-flex', alignItems: 'center', marginLeft: '4px', cursor: 'help' } }}
    >
        <InfoRegular style={{ fontSize: '14px', color: '#667eea' }} />
    </TooltipHost>
);

// FluentUI Styles matching Model Management
const styles = mergeStyleSets({
    container: {
        padding: '20px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #e1e1e1'
    },
    tileCard: {
        padding: '16px',
        margin: '8px 0',
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative',
        transition: 'all 0.2s ease',
        ':hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)'
        }
    },
    tileDisabled: {
        opacity: 0.6,
        backgroundColor: '#f5f5f5'
    },
    tileHeader: {
        borderBottom: '1px solid #f0f0f0',
        paddingBottom: '12px',
        marginBottom: '12px'
    },
    tileName: {
        fontSize: '16px',
        fontWeight: FontWeights.semibold,
        color: '#323130'
    },
    statusBadge: {
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: FontWeights.semibold
    },
    activeBadge: {
        backgroundColor: '#d4edda',
        color: '#155724'
    },
    inactiveBadge: {
        backgroundColor: '#f8d7da',
        color: '#721c24'
    },
    publicBadge: {
        backgroundColor: '#cce5ff',
        color: '#004085'
    },
    privateBadge: {
        backgroundColor: '#fff3cd',
        color: '#856404'
    },
    priorityBadge: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        backgroundColor: '#667eea',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '10px',
        fontSize: '10px',
        fontWeight: FontWeights.semibold
    },
    statusIndicator: {
        position: 'absolute',
        top: '8px',
        left: '8px',
        width: '8px',
        height: '8px',
        borderRadius: '50%'
    },
    statusEnabled: {
        backgroundColor: '#107c10'
    },
    statusDisabled: {
        backgroundColor: '#d13438'
    },
    tilesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '16px',
        marginTop: '16px'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '20px'
    },
    statCard: {
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e1e1e1',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statNumber: {
        fontSize: '24px',
        fontWeight: FontWeights.bold,
        color: '#323130'
    },
    statLabel: {
        fontSize: '12px',
        color: '#605e5c',
        marginTop: '4px'
    },
    tag: {
        display: 'inline-block',
        backgroundColor: '#f3f2f1',
        color: '#323130',
        padding: '2px 8px',
        margin: '2px',
        borderRadius: '12px',
        fontSize: '11px',
        border: '1px solid #e1dfdd'
    },
    actionButton: {
        marginLeft: '8px'
    },
    panelContent: {
        padding: '20px'
    }
});

const DataSourceTiles: React.FC = () => {
    const [tiles, setTiles] = useState<DataSourceTile[]>([]);
    const [searchIndexes, setSearchIndexes] = useState<SearchIndex[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);  // AI Foundry agents for web search
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
    const [showEditPanel, setShowEditPanel] = useState(false);
    const [editingTile, setEditingTile] = useState<DataSourceTile | null>(null);
    
    // Azure Blob Storage state
    const [storageContainers, setStorageContainers] = useState<{name: string; created_on?: string}[]>([]);
    const [showNewContainerDialog, setShowNewContainerDialog] = useState(false);
    const [newContainerName, setNewContainerName] = useState('');
    const [creatingContainer, setCreatingContainer] = useState(false);
    const [settingUpAgentic, setSettingUpAgentic] = useState(false);
    
    // File upload state
    const [showUploadPanel, setShowUploadPanel] = useState(false);
    const [uploadingTile, setUploadingTile] = useState<DataSourceTile | null>(null);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [containerFiles, setContainerFiles] = useState<{name: string; size?: number; last_modified?: string}[]>([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    
    // Agentic KB Instructions sync state
    const [syncingInstructions, setSyncingInstructions] = useState(false);
    
    // Intent embeddings sync state
    const [syncingEmbeddings, setSyncingEmbeddings] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [embeddingsStatus, setEmbeddingsStatus] = useState<{
        available: boolean;
        last_sync_time: string | null;
        prototype_count: number;
    } | null>(null);
    
    // Structured data sources state
    const [structuredSources, setStructuredSources] = useState<StructuredSource[]>([]);

    // Load intent embeddings status
    const loadEmbeddingsStatus = async () => {
        try {
            const response = await authenticatedFetch('/api/admin/intent-prototypes/status');
            const data = await response.json();
            if (data.success) {
                setEmbeddingsStatus({
                    available: data.available,
                    last_sync_time: data.last_sync_time,
                    prototype_count: data.prototype_count || 0
                });
            }
        } catch (error) {
            console.warn('Failed to load embeddings status:', error);
        }
    };

    // Load tiles from API
    const loadTiles = async () => {
        try {
            setLoading(true);
            const response = await authenticatedFetch('/api/admin/data-sources/tiles');
            const data = await response.json();
            
            if (data.success) {
                // Ensure all tiles have the required fields with defaults
                const processedTiles = (data.tiles || []).map((tile: any) => ({
                    ...tile,
                    web_urls: tile.web_urls || [],
                    azure_indexes: tile.azure_indexes || [],
                    keywords_en: tile.keywords_en || [],
                    keywords_he: tile.keywords_he || [],
                    examples: tile.examples || [],
                    source_privacy: tile.source_privacy || 'private'  // Default to private if not set
                }));
                
                setTiles(processedTiles);
                console.log('🔍 Loaded tiles:', processedTiles.length);
                console.log('📊 Tile details:', processedTiles.map((t: DataSourceTile) => ({
                    name: t.name,
                    type: t.connector_type,
                    is_default: t.is_default
                })));
            } else {
                setMessage({ text: data.error || 'Failed to load tiles', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to load data source tiles', type: MessageBarType.error });
        } finally {
            setLoading(false);
        }
    };

    // Load search indexes for Azure Search integration
    const loadSearchIndexes = async () => {
        try {
            const response = await authenticatedFetch('/api/admin/search-indexes');
            const data = await response.json();
            
            if (data.success) {
                setSearchIndexes(data.indexes || []);
                console.log('🔍 Loaded search indexes:', data.indexes?.length || 0);
            } else {
                console.warn('Failed to load search indexes:', data.error);
            }
        } catch (error) {
            console.warn('Failed to load search indexes:', error);
        }
    };

    // Load storage containers for Azure Blob integration
    const loadStorageContainers = async () => {
        try {
            const response = await authenticatedFetch('/api/agentic/storage/containers');
            const data = await response.json();
            
            if (data.success) {
                setStorageContainers(data.containers || []);
                console.log('📦 Loaded storage containers:', data.containers?.length || 0);
            } else {
                console.warn('Failed to load storage containers:', data.error);
            }
        } catch (error) {
            console.warn('Failed to load storage containers:', error);
        }
    };

    // Load AI Foundry agents for web search grounding
    const loadAgents = async () => {
        try {
            const response = await authenticatedFetch('/api/admin/agents');
            const data = await response.json();
            
            if (data.success) {
                setAgents(data.agents || []);
                console.log('🤖 Loaded agents:', data.agents?.length || 0);
            } else {
                console.warn('Failed to load agents:', data.error);
            }
        } catch (error) {
            console.warn('Failed to load agents:', error);
        }
    };

    // Load structured data sources
    const loadStructuredSources = async () => {
        try {
            const response = await authenticatedFetch('/api/admin/structured-sources');
            const data = await response.json();
            
            // Backend returns "connections" array
            const sources = data.connections || data.sources || [];
            if (sources.length > 0) {
                setStructuredSources(sources.map((s: any) => ({
                    id: s.id,
                    name: s.name,
                    description: s.description || '',
                    source_type: s.source_type,
                    is_active: s.is_active !== false, // Default to true
                    tables_count: s.tables_count || s.tables?.length || 0
                })));
                console.log('📊 Loaded structured sources:', sources.length);
            } else {
                console.log('📊 No structured sources found');
            }
        } catch (error) {
            console.warn('Failed to load structured sources:', error);
        }
    };

    // Create new storage container
    const createStorageContainer = async () => {
        if (!newContainerName.trim()) return;
        
        try {
            setCreatingContainer(true);
            const response = await authenticatedFetch('/api/agentic/storage/containers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newContainerName.toLowerCase().replace(/[^a-z0-9-]/g, '-') })
            });
            const data = await response.json();
            
            if (data.success) {
                setMessage({ text: `Container "${newContainerName}" created successfully`, type: MessageBarType.success });
                setShowNewContainerDialog(false);
                setNewContainerName('');
                await loadStorageContainers();
                // Select the new container
                if (editingTile) {
                    setEditingTile({ ...editingTile, blob_container: data.container.name });
                }
            } else {
                setMessage({ text: data.error || 'Failed to create container', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to create container', type: MessageBarType.error });
        } finally {
            setCreatingContainer(false);
        }
    };

    // Setup Agentic Retrieval for Azure Blob tile
    const setupAgenticRetrieval = async (tile: DataSourceTile): Promise<{ success: boolean, knowledge_source?: string, knowledge_base?: string }> => {
        if (!tile.blob_container || !tile.agentic_knowledge_base) {
            setMessage({ text: 'Container and Knowledge Base name are required', type: MessageBarType.error });
            return { success: false };
        }
        
        try {
            setSettingUpAgentic(true);
            const response = await authenticatedFetch('/api/agentic/knowledge/setup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    knowledge_base_name: tile.agentic_knowledge_base,
                    container_name: tile.blob_container,
                    description: tile.agentic_kb_description || `Knowledge base for ${tile.name}`,
                    reasoning_effort: tile.agentic_reasoning_effort || 'medium'
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setMessage({ text: 'Agentic Retrieval setup completed successfully', type: MessageBarType.success });
                // Return the actual created names from the API
                return { 
                    success: true, 
                    knowledge_source: data.knowledge_source,  // e.g., "myproject-source"
                    knowledge_base: data.knowledge_base       // e.g., "myproject-kb"
                };
            } else {
                setMessage({ text: data.error || 'Failed to setup Agentic Retrieval', type: MessageBarType.error });
                return { success: false };
            }
        } catch (error) {
            setMessage({ text: 'Failed to setup Agentic Retrieval', type: MessageBarType.error });
            return { success: false };
        } finally {
            setSettingUpAgentic(false);
        }
    };

    // Sync instructions to Azure Knowledge Base
    const syncInstructionsToKB = async () => {
        if (!editingTile?.agentic_knowledge_base) {
            setMessage({ text: 'Knowledge Base name is required to sync instructions', type: MessageBarType.error });
            return;
        }
        
        if (!editingTile.agentic_retrieval_instructions && !editingTile.agentic_answer_instructions) {
            setMessage({ text: 'At least one instruction (retrieval or answer) is required', type: MessageBarType.error });
            return;
        }
        
        try {
            setSyncingInstructions(true);
            const response = await authenticatedFetch(
                `/api/admin/agentic-retrieval/knowledge-bases/${encodeURIComponent(editingTile.agentic_knowledge_base)}/update-instructions`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        retrieval_instructions: editingTile.agentic_retrieval_instructions || '',
                        answer_instructions: editingTile.agentic_answer_instructions || '',
                        output_mode: 'answerSynthesis',
                        reasoning_effort: editingTile.agentic_reasoning_effort || 'medium'
                    })
                }
            );
            const data = await response.json();
            
            if (data.success) {
                setMessage({ 
                    text: `✅ Instructions synced to Knowledge Base "${editingTile.agentic_knowledge_base}"`, 
                    type: MessageBarType.success 
                });
            } else {
                setMessage({ 
                    text: data.error || 'Failed to sync instructions to Knowledge Base', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            console.error('Failed to sync instructions:', error);
            setMessage({ text: 'Failed to sync instructions to Knowledge Base', type: MessageBarType.error });
        } finally {
            setSyncingInstructions(false);
        }
    };

    // Load files from container
    const loadContainerFiles = async (containerName: string) => {
        try {
            setLoadingFiles(true);
            const response = await authenticatedFetch(`/api/agentic/storage/containers/${encodeURIComponent(containerName)}/files`);
            const data = await response.json();
            
            if (data.success) {
                setContainerFiles(data.files || []);
            } else {
                setMessage({ text: data.error || 'Failed to list files', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to list files', type: MessageBarType.error });
        } finally {
            setLoadingFiles(false);
        }
    };

    // Open upload panel for a tile
    const openUploadPanel = async (tile: DataSourceTile) => {
        setUploadingTile(tile);
        setShowUploadPanel(true);
        if (tile.blob_container) {
            await loadContainerFiles(tile.blob_container);
        }
    };

    // Handle file upload
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!uploadingTile?.blob_container || !event.target.files) return;
        
        const files = Array.from(event.target.files);
        if (files.length === 0) return;
        
        setUploadingFiles(true);
        let successCount = 0;
        let errorCount = 0;
        
        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('container', uploadingTile.blob_container);
                
                const response = await authenticatedFetch('/api/agentic/storage/upload', {
                    method: 'POST',
                    body: formData
                });
                
                const data = await response.json();
                if (data.success) {
                    successCount++;
                } else {
                    errorCount++;
                    console.error(`Failed to upload ${file.name}:`, data.error);
                }
            } catch (error) {
                errorCount++;
                console.error(`Failed to upload ${file.name}:`, error);
            }
        }
        
        setUploadingFiles(false);
        
        // Refresh file list
        if (uploadingTile.blob_container) {
            await loadContainerFiles(uploadingTile.blob_container);
        }
        
        if (errorCount === 0) {
            setMessage({ text: `Successfully uploaded ${successCount} file(s)`, type: MessageBarType.success });
        } else if (successCount > 0) {
            setMessage({ text: `Uploaded ${successCount} file(s), ${errorCount} failed`, type: MessageBarType.warning });
        } else {
            setMessage({ text: `Failed to upload files`, type: MessageBarType.error });
        }
        
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Trigger indexer after upload
    const triggerIndexer = async () => {
        if (!uploadingTile?.agentic_knowledge_source) {
            setMessage({ text: 'No knowledge source configured', type: MessageBarType.error });
            return;
        }
        
        try {
            setMessage({ text: 'Triggering indexer...', type: MessageBarType.info });
            // The indexer typically runs automatically, but we can trigger a refresh
            // For now, just show a message that the indexer will run
            setMessage({ 
                text: 'Files uploaded. The Azure AI Search indexer will automatically process new documents within a few minutes.', 
                type: MessageBarType.success 
            });
        } catch (error) {
            setMessage({ text: 'Failed to trigger indexer', type: MessageBarType.error });
        }
    };

    // Cache control functions for AI Intent Detection
    const refreshAiIntentCache = async () => {
        try {
            setMessage({ text: 'Refreshing AI Intent cache...', type: MessageBarType.info });
            
            const response = await authenticatedFetch('/api/admin/cache/ai-intent/refresh', {
                method: 'POST'
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Also invalidate frontend cache for data sources
                try {
                    const { cachedApiService } = await import('../../services/CachedApiService');
                    cachedApiService.invalidate('/api/data-sources');
                    console.log('[DataSourceTiles] Frontend data sources cache invalidated');
                } catch (e) {
                    console.warn('[DataSourceTiles] Failed to invalidate frontend cache:', e);
                }
                
                setMessage({ 
                    text: 'AI Intent cache refreshed successfully', 
                    type: MessageBarType.success 
                });
            } else {
                setMessage({ 
                    text: data.error || 'Failed to refresh AI Intent cache', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            setMessage({ 
                text: 'Failed to refresh AI Intent cache', 
                type: MessageBarType.error 
            });
        }
    };

    const getCacheStatus = async () => {
        try {
            const response = await authenticatedFetch('/api/admin/cache/ai-intent/status');
            const data = await response.json();
            
            if (data.success) {
                const status = data.cache_status;
                const statusText = `Cache Status:
• Config Loaded: ${status.config_loaded ? '✅' : '❌'}
• Data Sources: ${status.data_sources_count}
• Debug Mode: ${status.debug_mode ? '🔧' : '📴'}
• Cache TTL: ${status.cache_ttl_minutes} minutes
• Auto Refresh: ${status.auto_refresh_enabled ? '🔄' : '⏸️'}`;
                
                setMessage({ 
                    text: statusText, 
                    type: MessageBarType.info 
                });
            } else {
                setMessage({ 
                    text: data.error || 'Failed to get cache status', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            setMessage({ 
                text: 'Failed to get cache status', 
                type: MessageBarType.error 
            });
        }
    };
    
    // Sync intent embeddings for typo-tolerant keyword matching
    const syncIntentEmbeddings = async () => {
        try {
            setSyncingEmbeddings(true);
            setMessage({ text: 'Syncing intent embeddings...', type: MessageBarType.info });
            
            const response = await authenticatedFetch('/api/admin/intent-prototypes/sync', {
                method: 'POST'
            });
            
            const data = await response.json();
            
            if (data.success) {
                setMessage({ 
                    text: `Synced ${data.tile_prototypes} tile prototypes + ${data.meta_intent_prototypes} meta-intent prototypes`, 
                    type: MessageBarType.success 
                });
                // Refresh status after successful sync
                await loadEmbeddingsStatus();
            } else {
                setMessage({ 
                    text: data.error || 'Failed to sync intent embeddings', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            setMessage({ 
                text: 'Failed to sync intent embeddings', 
                type: MessageBarType.error 
            });
        } finally {
            setSyncingEmbeddings(false);
        }
    };

    useEffect(() => {
        loadTiles();
        loadSearchIndexes();
        loadStorageContainers();
        loadAgents();
        loadStructuredSources();
        loadEmbeddingsStatus();
    }, []);

    const openEditPanel = (tile?: DataSourceTile) => {
        if (tile) {
            // Ensure source_privacy and access fields are set (default values if missing from backend)
            setEditingTile({ 
                ...tile,
                source_privacy: tile.source_privacy || 'private',
                access_mode: tile.access_mode || 'all_users',
                allowed_users: tile.allowed_users || [],
                allowed_groups: tile.allowed_groups || []
            });
        } else {
            // Create new data source
            setEditingTile({
                id: '',
                name: '',
                description: '',
                icon: '🔍',
                connector_type: 'azure_search', // Default type
                keywords_en: [],
                keywords_he: [],
                azure_indexes: [],
                web_urls: [],
                agent_id: undefined,  // AI Foundry agent for custom web search
                confidence_threshold: 0.7,
                priority: 5,
                enabled: true,
                use_smart_search: true,
                fallback_enabled: true,
                source_privacy: 'private',  // Default to private for safety
                examples: [],
                is_default: false,
                is_grounding_source: false,
                enable_acl_filtering: false,
                // Data source access permissions
                access_mode: 'all_users',
                allowed_users: [],
                allowed_groups: [],
                grounding_system_message: '',
                grounding_no_results_message: '',
                // Agentic retrieval defaults
                use_agentic_retrieval: false,
                agentic_knowledge_base: '',
                agentic_user_description: '',
                agentic_retrieval_instructions: '',
                agentic_answer_instructions: '',
                agentic_output_mode: 'markdown',
                agentic_max_tokens: 4000,
                agentic_temperature: 0.0,
                agentic_max_output_size: 5000,
                agentic_reasoning_effort: 'low'
            });
        }
        setShowEditPanel(true);
    };

    const closeEditPanel = () => {
        setShowEditPanel(false);
        setEditingTile(null);
    };

    const saveTile = async () => {
        if (!editingTile) return;

        setIsSaving(true);
        let tileToSave = { ...editingTile };
        
        // Debug: Log source_privacy being saved
        console.log('📤 Saving tile with source_privacy:', tileToSave.source_privacy);
        // Debug: Log structured data source
        if (tileToSave.connector_type === 'structured_data') {
            console.log('📊 Saving structured_data tile:', {
                id: tileToSave.id,
                name: tileToSave.name,
                structured_source_id: tileToSave.structured_source_id,
                structured_source_name: tileToSave.structured_source_name
            });
        }

        try {
            // For azure_blob connector, setup agentic retrieval first
            if (editingTile.connector_type === 'azure_blob') {
                if (!editingTile.blob_container) {
                    setMessage({ text: 'Please select a storage container', type: MessageBarType.error });
                    return;
                }
                
                // Auto-generate knowledge base name if not provided
                const kbName = editingTile.agentic_knowledge_base || 
                    `${editingTile.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
                
                // Update the tile with agentic settings
                const updatedTile = {
                    ...editingTile,
                    use_agentic_retrieval: true,
                    agentic_knowledge_base: kbName
                };
                setEditingTile(updatedTile);
                
                // Call agentic setup API - it returns the actual created names
                const setupResult = await setupAgenticRetrieval(updatedTile);
                if (setupResult.success && setupResult.knowledge_source && setupResult.knowledge_base) {
                    // Store the actual created names (e.g., "myproject-source" and "myproject-kb")
                    tileToSave = {
                        ...updatedTile,
                        agentic_knowledge_source: setupResult.knowledge_source,
                        agentic_knowledge_base: setupResult.knowledge_base
                    };
                } else if (!setupResult.success) {
                    // Setup failed but we can still save the tile - user can retry later
                    console.warn('Agentic setup failed, saving tile anyway');
                    tileToSave = updatedTile;
                }
            }

            const isNew = !tileToSave.id || tileToSave.id === '';
            const url = isNew 
                ? '/api/admin/data-sources/tiles'
                : `/api/admin/data-sources/tiles/${tileToSave.id}`;
            const method = isNew ? 'POST' : 'PUT';

            const response = await authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tileToSave)
            });

            const data = await response.json();
            
            if (data.success) {
                await loadTiles();
                closeEditPanel();
                setMessage({ 
                    text: `Data source ${isNew ? 'created' : 'updated'} successfully`, 
                    type: MessageBarType.success 
                });
            } else {
                setMessage({ text: data.error || 'Failed to save data source', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to save data source', type: MessageBarType.error });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteTile = async (id: string) => {
        const tile = tiles.find(t => t.id === id);
        if (tile?.is_default) {
            setMessage({ text: 'Cannot delete default data sources', type: MessageBarType.warning });
            return;
        }

        if (!confirm('Are you sure you want to delete this data source?')) return;

        try {
            const response = await authenticatedFetch(`/api/admin/data-sources/tiles/${id}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            
            if (data.success) {
                await loadTiles();
                setMessage({ text: 'Data source deleted successfully', type: MessageBarType.success });
            } else {
                setMessage({ text: data.error || 'Failed to delete data source', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to delete data source', type: MessageBarType.error });
        }
    };

    const toggleTileEnabled = async (id: string, enabled: boolean) => {
        try {
            const response = await authenticatedFetch(`/api/admin/data-sources/tiles/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled })
            });

            const data = await response.json();
            
            if (data.success) {
                await loadTiles();
            } else {
                setMessage({ text: data.error || 'Failed to update data source', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to update data source', type: MessageBarType.error });
        }
    };

    const updatePriority = async (id: string, priority: number) => {
        try {
            const response = await authenticatedFetch(`/api/admin/data-sources/tiles/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ priority })
            });

            const data = await response.json();
            
            if (data.success) {
                await loadTiles();
                setMessage({ text: 'Priority updated successfully', type: MessageBarType.success });
            } else {
                setMessage({ text: data.error || 'Failed to update priority', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Failed to update priority', type: MessageBarType.error });
        }
    };

    const renderTileCard = (tile: DataSourceTile) => {
        const getConnectorIcon = (type: string) => {
            switch (type) {
                case 'sharepoint': 
                    return <ShareRegular style={{ fontSize: 20, color: '#667eea' }} />;
                case 'office365':
                    if (tile.id.includes('email')) {
                        return <MailRegular style={{ fontSize: 20, color: '#667eea' }} />;
                    } else if (tile.id.includes('calendar')) {
                        return <CalendarRegular style={{ fontSize: 20, color: '#667eea' }} />;
                    } else {
                        return <FolderRegular style={{ fontSize: 20, color: '#667eea' }} />;
                    }
                case 'google_workspace':
                    if (tile.id.includes('gmail')) {
                        return <MailRegular style={{ fontSize: 20, color: '#ea4335' }} />;
                    } else if (tile.id.includes('calendar')) {
                        return <CalendarRegular style={{ fontSize: 20, color: '#4285f4' }} />;
                    } else if (tile.id.includes('drive')) {
                        return <FolderRegular style={{ fontSize: 20, color: '#34a853' }} />;
                    } else {
                        return <CloudRegular style={{ fontSize: 20, color: '#fbbc04' }} />;
                    }
                case 'azure_search':
                    return <SearchRegular style={{ fontSize: 20, color: '#667eea' }} />;
                case 'azure_blob':
                    return <CloudRegular style={{ fontSize: 20, color: '#16a085' }} />;
                case 'document_translation':
                    return <Icon iconName="LocaleLanguage" style={{ fontSize: 20, color: '#9b59b6' }} />;
                case 'general_knowledge':
                case 'internal_knowledge':  // Legacy
                    return <DatabaseRegular style={{ fontSize: 20, color: '#667eea' }} />;
                case 'live_search':
                    return <CloudRegular style={{ fontSize: 20, color: '#667eea' }} />;
                case 'file_server':
                    return <FolderRegular style={{ fontSize: 20, color: '#667eea' }} />;
                case 'structured_data':
                    return <TableRegular style={{ fontSize: 20, color: '#27ae60' }} />;
                default: 
                    return <CloudRegular style={{ fontSize: 20, color: '#667eea' }} />;
            }
        };

        const getConnectorTypeLabel = (type: string) => {
            switch (type) {
                case 'sharepoint': return 'SharePoint';
                case 'office365': return 'Office 365';
                case 'google_workspace': return 'Google Workspace';
                case 'azure_search': return 'Azure Search Index';
                case 'azure_blob': return 'Azure Blob Storage';
                case 'document_translation': return 'Document Translation';
                case 'general_knowledge': return 'AI Assistant (General Knowledge)';
                case 'internal_knowledge': return 'AI Assistant (General Knowledge)';  // Legacy
                case 'live_search': return 'Live Search (Grounding)';
                case 'file_server': return 'File Server';
                case 'structured_data': return 'Structured Data (Excel/SQL)';
                default: return 'Custom';
            }
        };

        return (
            <div key={tile.id} className={`${styles.tileCard} ${!tile.enabled ? styles.tileDisabled : ''}`}>
                {/* Status indicator */}
                <div className={`${styles.statusIndicator} ${tile.enabled ? styles.statusEnabled : styles.statusDisabled}`} />
                
                {/* Priority badge */}
                <div className={styles.priorityBadge}>P{tile.priority}</div>

                <div className={styles.tileHeader}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                        {getConnectorIcon(tile.connector_type)}
                        <Stack style={{ flex: 1 }}>
                            <Text className={styles.tileName}>{tile.name}</Text>
                            <Text variant="small" style={{ color: '#666' }}>{tile.description}</Text>
                            {tile.connector_type === 'azure_search' && tile.azure_indexes.length > 0 && (
                                <Text variant="xSmall" style={{ color: '#667eea', fontWeight: FontWeights.semibold }}>
                                    📊 {tile.azure_indexes[0]}
                                </Text>
                            )}
                        </Stack>
                        <Stack tokens={{ childrenGap: 5 }}>
                            {tile.connector_type === 'azure_search' && tile.is_grounding_source && (
                                <div className={styles.statusBadge} style={{ backgroundColor: '#8b5cf6', color: 'white', fontSize: '10px' }}>
                                    🎯 Grounding
                                </div>
                            )}
                            <TooltipHost
                                content={
                                    tile.source_privacy === 'public' 
                                        ? "PUBLIC: Q&A pairs from this source can be cached in cross-organization FAQ for faster responses."
                                        : "PRIVATE: Contains sensitive data. Q&A pairs are never cached or shared."
                                }
                                directionalHint={DirectionalHint.topCenter}
                            >
                                <div className={`${styles.statusBadge} ${tile.source_privacy === 'public' ? styles.publicBadge : styles.privateBadge}`} style={{ cursor: 'help' }}>
                                    {tile.source_privacy === 'public' ? '🌐 Public' : '🔒 Private'}
                                </div>
                            </TooltipHost>
                            <div className={`${styles.statusBadge} ${tile.enabled ? styles.activeBadge : styles.inactiveBadge}`}>
                                {tile.enabled ? 'Active' : 'Inactive'}
                            </div>
                        </Stack>
                    </Stack>
                </div>

                <Stack tokens={{ childrenGap: 8 }}>
                    <Stack horizontal horizontalAlign="space-between">
                        <Text variant="small" style={{ color: '#666' }}>
                            {getConnectorTypeLabel(tile.connector_type)}
                        </Text>
                        <Text variant="small" style={{ color: '#666' }}>
                            Confidence: {(tile.confidence_threshold * 100).toFixed(0)}%
                        </Text>
                    </Stack>
                    
                    {tile.azure_indexes.length > 0 && (
                        <Stack>
                            <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Azure Indexes:</Text>
                            <div>
                                {tile.azure_indexes.map((index, idx) => (
                                    <span key={idx} className={styles.tag} style={{ backgroundColor: '#e6f3ff' }}>{index}</span>
                                ))}
                            </div>
                        </Stack>
                    )}

                    {tile.keywords_en.length > 0 && (
                        <Stack>
                            <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Keywords:</Text>
                            <div>
                                {tile.keywords_en.slice(0, 5).map((keyword, idx) => (
                                    <span key={idx} className={styles.tag}>{keyword}</span>
                                ))}
                                {tile.keywords_en.length > 5 && (
                                    <span className={styles.tag}>+{tile.keywords_en.length - 5} more</span>
                                )}
                            </div>
                        </Stack>
                    )}

                    {/* Azure Blob specific info */}
                    {tile.connector_type === 'azure_blob' && tile.blob_container && (
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Container:</Text>
                            <Text variant="small" style={{ color: '#667eea' }}>📦 {tile.blob_container}</Text>
                            {tile.agentic_knowledge_base && (
                                <Text variant="xSmall" style={{ color: '#16a085' }}>🤖 KB: {tile.agentic_knowledge_base}</Text>
                            )}
                        </Stack>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign="space-between">
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <Checkbox
                                checked={tile.enabled}
                                onChange={(_, checked) => toggleTileEnabled(tile.id, checked || false)}
                                label="Enabled"
                            />
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            {/* Upload button for azure_blob tiles */}
                            {tile.connector_type === 'azure_blob' && tile.blob_container && (
                                <IconButton
                                    iconProps={{ iconName: 'Upload' }}
                                    title="Upload files to container"
                                    onClick={() => openUploadPanel(tile)}
                                    className={styles.actionButton}
                                    styles={{ root: { color: '#16a085' } }}
                                />
                            )}
                            <Dropdown
                                selectedKey={tile.priority}
                                onChange={(_, option) => option && updatePriority(tile.id, option.key as number)}
                                options={[
                                    { key: 1, text: 'P1' },
                                    { key: 2, text: 'P2' },
                                    { key: 3, text: 'P3' },
                                    { key: 4, text: 'P4' },
                                    { key: 5, text: 'P5' }
                                ]}
                                styles={{ dropdown: { width: 60 } }}
                                title="Priority"
                            />
                            <IconButton
                                iconProps={{ iconName: 'Edit' }}
                                title="Edit data source"
                                onClick={() => openEditPanel(tile)}
                                className={styles.actionButton}
                            />
                            {!tile.is_default && (
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    title="Delete data source"
                                    onClick={() => deleteTile(tile.id)}
                                    className={styles.actionButton}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </div>
        );
    };

    const stats = {
        total: tiles.length,
        active: tiles.filter(t => t.enabled).length,
        inactive: tiles.filter(t => !t.enabled).length,
        default: tiles.filter(t => t.is_default).length
    };

    return (
        <div className={styles.container}>
            <Stack tokens={stackTokens}>
                {message && (
                    <MessageBar 
                        messageBarType={message.type}
                        onDismiss={() => setMessage(null)}
                    >
                        {message.text}
                    </MessageBar>
                )}

                {/* Stats Grid */}
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statNumber}>{stats.total}</div>
                        <div className={styles.statLabel}>Total Tiles</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statNumber}>{stats.active}</div>
                        <div className={styles.statLabel}>Active</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statNumber}>{stats.inactive}</div>
                        <div className={styles.statLabel}>Inactive</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statNumber}>{stats.default}</div>
                        <div className={styles.statLabel}>Default</div>
                    </div>
                </div>

                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap>
                    <Stack>
                        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
                            Data Sources & Search Indexes ({tiles.length})
                        </Text>
                        <Text variant="small" style={{ color: '#605e5c' }}>
                            Manage SharePoint, Office 365, Google Workspace, File Servers, and Azure Search integrations
                        </Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                        {/* Embeddings Status Card */}
                        <Stack 
                            horizontal 
                            verticalAlign="center" 
                            tokens={{ childrenGap: 8 }}
                            style={{
                                backgroundColor: embeddingsStatus?.prototype_count ? '#f3f9f4' : '#fff4ce',
                                border: `1px solid ${embeddingsStatus?.prototype_count ? '#107c10' : '#ffb900'}`,
                                borderRadius: '4px',
                                padding: '6px 12px',
                            }}
                        >
                            <Icon 
                                iconName={embeddingsStatus?.prototype_count ? 'CheckMark' : 'Warning'} 
                                style={{ 
                                    color: embeddingsStatus?.prototype_count ? '#107c10' : '#ffb900',
                                    fontSize: '14px'
                                }} 
                            />
                            <Stack tokens={{ childrenGap: 0 }}>
                                <Text variant="small" style={{ fontWeight: FontWeights.semibold, color: '#323130' }}>
                                    {embeddingsStatus?.prototype_count || 0} embeddings
                                </Text>
                                <Text variant="tiny" style={{ color: '#605e5c' }}>
                                    {embeddingsStatus?.last_sync_time 
                                        ? `Synced ${new Date(embeddingsStatus.last_sync_time).toLocaleTimeString()}`
                                        : embeddingsStatus?.prototype_count 
                                            ? 'Previously indexed'
                                            : 'Not synced'
                                    }
                                </Text>
                            </Stack>
                            <DefaultButton
                                iconProps={{ iconName: 'Sync' }}
                                onClick={syncIntentEmbeddings}
                                disabled={syncingEmbeddings}
                                style={{ minWidth: 'auto', padding: '0 8px' }}
                                title="Sync intent embeddings for typo-tolerant keyword matching"
                            >
                                {syncingEmbeddings ? (
                                    <Spinner size={SpinnerSize.xSmall} />
                                ) : (
                                    <span style={{ fontSize: '12px' }}>Sync</span>
                                )}
                            </DefaultButton>
                        </Stack>
                        
                        <Separator vertical style={{ height: '32px' }} />
                        
                        <DefaultButton
                            iconProps={{ iconName: 'Refresh' }}
                            onClick={refreshAiIntentCache}
                            text="Refresh Cache"
                            title="Force refresh AI Intent cache to apply tile configuration changes"
                        />
                        <DefaultButton
                            iconProps={{ iconName: 'Info' }}
                            onClick={getCacheStatus}
                            text="Cache Status"
                            title="Check current AI Intent cache status"
                        />
                        <PrimaryButton
                            iconProps={{ iconName: 'Add' }}
                            onClick={() => openEditPanel()}
                            text="Add Data Source"
                        />
                    </Stack>
                </Stack>

                {loading ? (
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 10 }}>
                        <Spinner size={SpinnerSize.medium} />
                        <Text>Loading tiles...</Text>
                    </Stack>
                ) : (
                    <div className={styles.tilesGrid}>
                        {tiles.map(renderTileCard)}
                    </div>
                )}

                {/* Edit Panel */}
                <Panel
                    isOpen={showEditPanel}
                    onDismiss={closeEditPanel}
                    type={PanelType.medium}
                    headerText={editingTile?.id ? 'Edit Data Source' : 'Add New Data Source'}
                    closeButtonAriaLabel="Close"
                >
                    {editingTile && (
                        <div className={styles.panelContent}>
                            <Stack tokens={{ childrenGap: 15 }}>
                                <TextField
                                    label="Name"
                                    value={editingTile.name}
                                    onChange={(_, value) => setEditingTile({ ...editingTile, name: value || '' })}
                                    required
                                    placeholder="e.g., Company Documents, Project Files"
                                />
                                
                                <TextField
                                    label="Description"
                                    value={editingTile.description}
                                    onChange={(_, value) => setEditingTile({ ...editingTile, description: value || '' })}
                                    multiline
                                    rows={3}
                                    placeholder="Describe what content this data source contains"
                                />

                                {!editingTile.is_default && (
                                    <Dropdown
                                        label="Integration Type"
                                        selectedKey={editingTile.connector_type}
                                        onChange={(_, option) => option && setEditingTile({ ...editingTile, connector_type: option.key as string })}
                                        options={[
                                            { key: 'azure_search', text: 'Azure Search Index' },
                                            { key: 'azure_blob', text: 'Azure Blob Storage (Agentic)' },
                                            { key: 'structured_data', text: 'Structured Data (Excel/SQL)' },
                                            { key: 'document_translation', text: 'Document Translation (Azure AI)' },
                                            { key: 'sharepoint', text: 'SharePoint (Coming Soon)', disabled: true },
                                            { key: 'office365', text: 'Office 365 (Coming Soon)', disabled: true },
                                            { key: 'google_workspace', text: 'Google Workspace (Available)', disabled: false },
                                            { key: 'file_server', text: 'File Server (Coming Soon)', disabled: true },
                                            { key: 'general_knowledge', text: 'AI Assistant (General Knowledge)' },
                                            { key: 'live_search', text: 'Live Search' },
                                            { key: 'real_time_web', text: 'Real-Time Web Search' }
                                        ]}
                                    />
                                )}

                                {editingTile.connector_type === 'azure_search' && (
                                    <ComboBox
                                        label="Azure Search Indexes"
                                        selectedKey={editingTile.azure_indexes.length > 0 ? editingTile.azure_indexes[0] : undefined}
                                        onChange={(_, option) => {
                                            if (option) {
                                                setEditingTile({ 
                                                    ...editingTile, 
                                                    azure_indexes: [option.key as string]
                                                });
                                            }
                                        }}
                                        options={searchIndexes.map(index => ({
                                            key: index.name,
                                            text: `${index.name}${index.description ? ` - ${index.description}` : ''}`
                                        }))}
                                        placeholder="Select an Azure Search index"
                                        allowFreeform
                                        autoComplete="on"
                                    />
                                )}

                                {editingTile.connector_type === 'structured_data' && (
                                    <Stack tokens={{ childrenGap: 15 }} styles={{ root: { padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '4px', border: '1px solid #a5d6a7' } }}>
                                        <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold, color: '#27ae60' } }}>
                                            📊 Structured Data Configuration
                                        </Text>
                                        
                                        <Dropdown
                                            label="Select Structured Data Source"
                                            selectedKey={editingTile.structured_source_id || ''}
                                            onChange={(_, option) => {
                                                console.log('📊 Dropdown onChange:', { key: option?.key, text: option?.text });
                                                const selectedSource = structuredSources.find(s => s.id === option?.key);
                                                console.log('📊 Found source:', selectedSource);
                                                const newTile = { 
                                                    ...editingTile, 
                                                    structured_source_id: option?.key as string || undefined,
                                                    structured_source_name: selectedSource?.name || option?.text?.split(' (')[0]
                                                };
                                                console.log('📊 Setting editingTile with structured_source_id:', newTile.structured_source_id);
                                                setEditingTile(newTile);
                                            }}
                                            options={(() => {
                                                const opts = structuredSources.map(source => ({
                                                    key: source.id,
                                                    text: `${source.name} (${source.source_type})`
                                                }));
                                                // Add fallback option if we have a saved ID but sources haven't loaded yet
                                                if (editingTile.structured_source_id && structuredSources.length === 0 && editingTile.structured_source_name) {
                                                    opts.push({
                                                        key: editingTile.structured_source_id,
                                                        text: `${editingTile.structured_source_name} (loading...)`
                                                    });
                                                }
                                                return opts;
                                            })()}
                                            placeholder="Select a structured data source..."
                                        />
                                        
                                        {structuredSources.length === 0 && !editingTile.structured_source_id && (
                                            <MessageBar messageBarType={MessageBarType.info}>
                                                <Text variant="small">
                                                    No structured data sources configured. Go to <strong>Structured Data Sources</strong> to upload Excel files or connect SQL databases.
                                                </Text>
                                            </MessageBar>
                                        )}
                                        
                                        {editingTile.structured_source_id && (() => {
                                            const selectedSource = structuredSources.find(s => s.id === editingTile.structured_source_id);
                                            // Use saved structured_source_name as fallback if sources list hasn't loaded yet
                                            const displayName = selectedSource?.name || editingTile.structured_source_name;
                                            if (displayName) {
                                                return (
                                                    <MessageBar messageBarType={MessageBarType.success} styles={{ root: { marginTop: 8 } }}>
                                                        <Text variant="small">
                                                            <strong>Selected:</strong> {displayName}
                                                            {selectedSource?.description && ` - ${selectedSource.description}`}
                                                            {selectedSource?.tables_count !== undefined && ` (${selectedSource.tables_count} tables/sheets)`}
                                                            {!selectedSource && structuredSources.length === 0 && ' (loading details...)'}
                                                        </Text>
                                                    </MessageBar>
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        <Text variant="small" styles={{ root: { color: '#666', fontStyle: 'italic' } }}>
                                            When users ask questions matching your keywords, the AI will query this structured data source (Excel or SQL) to provide answers.
                                        </Text>
                                    </Stack>
                                )}

                                {editingTile.connector_type === 'real_time_web' && (
                                    <Stack tokens={{ childrenGap: 15 }} styles={{ root: { padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px', border: '1px solid #e1dfdd' } }}>
                                        <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold, color: '#667eea' } }}>
                                            🌐 Web Search Configuration
                                        </Text>
                                        
                                        <Dropdown
                                            label="AI Agent for Web Search"
                                            selectedKey={editingTile.agent_id || ''}
                                            onChange={(_, option) => setEditingTile({ 
                                                ...editingTile, 
                                                agent_id: option?.key === '' ? undefined : option?.key as string,
                                                // Clear web_urls when selecting a custom agent (URLs come from agent's config)
                                                web_urls: option?.key === '' ? editingTile.web_urls : []
                                            })}
                                            options={[
                                                { key: '', text: '(Default Agent - General Web Search)' },
                                                ...agents.map(agent => ({
                                                    key: agent.id,
                                                    text: `${agent.name}${agent.grounding_type ? ` (${agent.grounding_type})` : ''}`
                                                }))
                                            ]}
                                            placeholder="Select an agent for custom web search"
                                        />
                                        
                                        {/* Show custom search config info when agent uses bing_custom_search */}
                                        {editingTile.agent_id && (() => {
                                            const selectedAgent = agents.find(a => a.id === editingTile.agent_id);
                                            if (selectedAgent?.grounding_type === 'bing_custom_search' && selectedAgent.custom_search_config_name) {
                                                return (
                                                    <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: 8 } }}>
                                                        <Text variant="small">
                                                            <strong>Domain Restrictions:</strong> This agent uses the "{selectedAgent.custom_search_config_name}" custom search configuration. 
                                                            Edit domain restrictions in Admin → Agent Management → Custom Search Configs.
                                                        </Text>
                                                    </MessageBar>
                                                );
                                            } else if (selectedAgent?.grounding_type === 'bing_search') {
                                                return (
                                                    <Text variant="small" styles={{ root: { color: '#666', marginTop: 8 } }}>
                                                        This agent uses general Bing Search (no domain restrictions).
                                                    </Text>
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Only show URL input when using default agent (no custom agent selected) */}
                                        {!editingTile.agent_id && (
                                            <>
                                                <Text variant="small" styles={{ root: { color: '#666' } }}>
                                                    Select a custom Bing Search agent to restrict searches to specific domains. 
                                                    Create agents with domain restrictions in Admin → Agent Management.
                                                </Text>
                                                
                                                <TagInput
                                                    label="Focus URLs (Optional)"
                                                    value={editingTile.web_urls || []}
                                                    onChange={(urls) => setEditingTile({ 
                                                        ...editingTile, 
                                                        web_urls: urls
                                                    })}
                                                    placeholder="Enter URLs to focus search: https://example.com, https://news.example.com"
                                                    description="When using the default agent, you can optionally specify URLs to focus the search. For strict domain restrictions, create a custom agent with Bing Custom Search."
                                                    maxTags={10}
                                                />
                                            </>
                                        )}
                                    </Stack>
                                )}

                                {/* Azure Blob Storage Configuration */}
                                {editingTile.connector_type === 'azure_blob' && (
                                    <Stack tokens={{ childrenGap: 15 }} styles={{ root: { padding: '15px', backgroundColor: '#f3f2f1', borderRadius: '4px', border: '1px solid #e1dfdd', maxWidth: '100%', overflow: 'hidden' } }}>
                                        <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold, color: '#667eea' } }}>
                                            📦 Azure Blob Storage Configuration
                                        </Text>
                                        
                                        {!showNewContainerDialog ? (
                                            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end" styles={{ root: { width: '100%' } }}>
                                                <Dropdown
                                                    label="Storage Container"
                                                    selectedKey={editingTile.blob_container}
                                                    onChange={(_, option) => {
                                                        if (option?.key === '__create_new__') {
                                                            setShowNewContainerDialog(true);
                                                        } else if (option) {
                                                            setEditingTile({ ...editingTile, blob_container: option.key as string });
                                                        }
                                                    }}
                                                    options={[
                                                        ...storageContainers.map(c => ({ key: c.name, text: c.name || '(unnamed)' })),
                                                        { key: '__create_new__', text: '➕ Create New Container...' }
                                                    ]}
                                                    placeholder="Select a container"
                                                    styles={{ root: { flex: 1, minWidth: 0 }, dropdown: { backgroundColor: 'white' } }}
                                                />
                                                <DefaultButton
                                                    text="New"
                                                    iconProps={{ iconName: 'Add' }}
                                                    onClick={() => setShowNewContainerDialog(true)}
                                                    styles={{ root: { minWidth: 60, flexShrink: 0 } }}
                                                />
                                            </Stack>
                                        ) : (
                                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '10px', backgroundColor: '#e8f4f8', borderRadius: '4px', border: '1px solid #b3d9e8' } }}>
                                                <Text styles={{ root: { fontWeight: FontWeights.semibold } }}>Create New Container</Text>
                                                <TextField
                                                    label="Container Name"
                                                    value={newContainerName}
                                                    onChange={(_, value) => setNewContainerName(value || '')}
                                                    placeholder="my-documents"
                                                    description="Lowercase, letters, numbers, and hyphens only"
                                                    disabled={creatingContainer}
                                                    styles={{ fieldGroup: { backgroundColor: 'white' } }}
                                                />
                                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                                    <PrimaryButton 
                                                        text={creatingContainer ? "Creating..." : "Create"} 
                                                        onClick={createStorageContainer}
                                                        disabled={!newContainerName.trim() || creatingContainer}
                                                    />
                                                    <DefaultButton 
                                                        text="Cancel" 
                                                        onClick={() => {
                                                            setShowNewContainerDialog(false);
                                                            setNewContainerName('');
                                                        }}
                                                        disabled={creatingContainer}
                                                    />
                                                </Stack>
                                            </Stack>
                                        )}
                                        
                                        <TextField
                                            label="Folder Prefix (Optional)"
                                            value={editingTile.blob_prefix || ''}
                                            onChange={(_, value) => setEditingTile({ 
                                                ...editingTile, 
                                                blob_prefix: value || ''
                                            })}
                                            placeholder="documents/reports/"
                                            description="Limit indexing to files within this folder path"
                                            styles={{ fieldGroup: { backgroundColor: 'white' } }}
                                        />
                                        
                                        <Separator />
                                        
                                        <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold, color: '#16a085' } }}>
                                            🤖 Agentic Retrieval Setup
                                        </Text>
                                        
                                        <TextField
                                            label="Knowledge Base Name"
                                            value={editingTile.agentic_knowledge_base || ''}
                                            onChange={(_, value) => {
                                                // Normalize to lowercase with hyphens only
                                                const normalized = (value || '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
                                                setEditingTile({ 
                                                    ...editingTile, 
                                                    agentic_knowledge_base: normalized,
                                                    use_agentic_retrieval: true
                                                });
                                            }}
                                            placeholder="my-documents-kb"
                                            description="Lowercase, letters, numbers, and hyphens only (Azure requirement)"
                                            required
                                            styles={{ fieldGroup: { backgroundColor: 'white' } }}
                                        />
                                        
                                        <TextField
                                            label="Description"
                                            value={editingTile.agentic_kb_description || ''}
                                            onChange={(_, value) => setEditingTile({ 
                                                ...editingTile, 
                                                agentic_kb_description: value || ''
                                            })}
                                            placeholder="Documents for company reports and policies"
                                            multiline
                                            rows={2}
                                            styles={{ fieldGroup: { backgroundColor: 'white' } }}
                                        />
                                        
                                        <Dropdown
                                            label="Reasoning Effort"
                                            selectedKey={editingTile.agentic_reasoning_effort || 'medium'}
                                            onChange={(_, option) => option && setEditingTile({ 
                                                ...editingTile, 
                                                agentic_reasoning_effort: option.key as string 
                                            })}
                                            options={[
                                                { key: 'low', text: 'Low - Fast, simple queries' },
                                                { key: 'medium', text: 'Medium - Balanced (Recommended)' },
                                                { key: 'high', text: 'High - Complex, multi-document analysis' }
                                            ]}
                                            styles={{ dropdown: { backgroundColor: 'white' } }}
                                        />
                                        
                                        {settingUpAgentic && (
                                            <MessageBar messageBarType={MessageBarType.info}>
                                                Setting up Agentic Retrieval... This may take a few moments.
                                            </MessageBar>
                                        )}
                                    </Stack>
                                )}

                                {/* Document Translation Configuration */}
                                {editingTile.connector_type === 'document_translation' && (
                                    <Stack tokens={{ childrenGap: 15 }} styles={{ root: { padding: '15px', backgroundColor: '#f5f0fa', borderRadius: '4px', border: '1px solid #d4b8e8' } }}>
                                        <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold, color: '#9b59b6' } }}>
                                            🌐 Document Translation Configuration
                                        </Text>
                                        
                                        <MessageBar messageBarType={MessageBarType.info}>
                                            <Text variant="small">
                                                This data source enables AI-powered document translation. Users can upload documents and request translations in chat.
                                                The target language is auto-detected from the user's message (e.g., "translate to Hebrew").
                                            </Text>
                                        </MessageBar>
                                        
                                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end" styles={{ root: { width: '100%' } }}>
                                            <Dropdown
                                                label="Output Container"
                                                selectedKey={editingTile.translation_output_container || ''}
                                                onChange={(_, option) => {
                                                    if (option?.key === '__create_new__') {
                                                        setShowNewContainerDialog(true);
                                                    } else if (option) {
                                                        setEditingTile({ ...editingTile, translation_output_container: option.key as string });
                                                    }
                                                }}
                                                options={[
                                                    ...storageContainers.map(c => ({ key: c.name, text: c.name || '(unnamed)' })),
                                                    { key: '__create_new__', text: '➕ Create New Container...' }
                                                ]}
                                                placeholder="Select output container for translated files"
                                                styles={{ root: { flex: 1, minWidth: 0 }, dropdown: { backgroundColor: 'white' } }}
                                            />
                                        </Stack>
                                        
                                        <Dropdown
                                            label="Default Target Language"
                                            selectedKey={editingTile.translation_default_language || 'he'}
                                            onChange={(_, option) => option && setEditingTile({ 
                                                ...editingTile, 
                                                translation_default_language: option.key as string 
                                            })}
                                            options={[
                                                { key: 'he', text: 'Hebrew (עברית)' },
                                                { key: 'en', text: 'English' },
                                                { key: 'ar', text: 'Arabic (العربية)' },
                                                { key: 'fr', text: 'French (Français)' },
                                                { key: 'de', text: 'German (Deutsch)' },
                                                { key: 'es', text: 'Spanish (Español)' },
                                                { key: 'ru', text: 'Russian (Русский)' },
                                                { key: 'zh-Hans', text: 'Chinese Simplified (简体中文)' }
                                            ]}
                                            styles={{ dropdown: { backgroundColor: 'white' } }}
                                        />
                                        
                                        <Text variant="small" styles={{ root: { color: '#666' } }}>
                                            Supported formats: PDF, DOCX, PPTX, XLSX, HTML, TXT, RTF, and more.
                                            Maximum file size depends on your Azure AI Services tier.
                                        </Text>
                                    </Stack>
                                )}

                                <TagInput
                                    label="Keywords"
                                    value={editingTile.keywords_en}
                                    onChange={(keywords) => setEditingTile({ 
                                        ...editingTile, 
                                        keywords_en: keywords
                                    })}
                                    placeholder="Enter keywords separated by commas: bank, profit, audit, risk management"
                                    description="Keywords help the AI understand when to use this data source. You can enter multiple keywords separated by commas, or press Enter to add individual keywords."
                                    maxTags={50}
                                />

                                <TagInput
                                    label="Hebrew Keywords (מילות מפתח בעברית)"
                                    value={editingTile.keywords_he}
                                    onChange={(keywords) => setEditingTile({ 
                                        ...editingTile, 
                                        keywords_he: keywords
                                    })}
                                    placeholder="הזן מילות מפתח מופרדות בפסיק: בנק, רווח, ביקורת, ניהול סיכונים"
                                    description="מילות מפתח בעברית עוזרות לבינה המלאכותית להבין מתי להשתמש במקור נתונים זה. ניתן להזין מילות מפתח מרובות מופרדות בפסיקים."
                                    maxTags={50}
                                />

                                <Stack horizontal tokens={{ childrenGap: 15 }}>
                                    <TextField
                                        label="Confidence Threshold"
                                        type="number"
                                        min={0}
                                        max={1}
                                        step={0.1}
                                        value={editingTile.confidence_threshold.toString()}
                                        onChange={(_, value) => setEditingTile({ 
                                            ...editingTile, 
                                            confidence_threshold: parseFloat(value || '0.7')
                                        })}
                                        styles={{ root: { width: '50%' } }}
                                    />

                                    <Dropdown
                                        label="Priority"
                                        selectedKey={editingTile.priority}
                                        onChange={(_, option) => option && setEditingTile({ 
                                            ...editingTile, 
                                            priority: option.key as number
                                        })}
                                        options={[
                                            { key: 1, text: 'Priority 1 (Highest)' },
                                            { key: 2, text: 'Priority 2 (High)' },
                                            { key: 3, text: 'Priority 3 (Medium)' },
                                            { key: 4, text: 'Priority 4 (Low)' },
                                            { key: 5, text: 'Priority 5 (Lowest)' }
                                        ]}
                                        styles={{ root: { width: '50%' } }}
                                    />
                                </Stack>

                                {/* Access Control Section - applies to all data sources */}
                                <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: '15px', backgroundColor: '#f5f9ff', borderRadius: '4px', border: '1px solid #c7e0f4' } }}>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, color: '#667eea' }}>
                                            🔐 Access Control
                                        </Text>
                                        <TooltipHost
                                            content="Control which users can see and use this data source. Users without access won't see this source in their selector or have queries routed to it."
                                            directionalHint={DirectionalHint.topCenter}
                                        >
                                            <Icon iconName="Info" styles={{ root: { color: '#667eea', cursor: 'pointer' } }} />
                                        </TooltipHost>
                                    </Stack>
                                    
                                    <Dropdown
                                        label="Access Mode"
                                        selectedKey={editingTile.access_mode || 'all_users'}
                                        onChange={(_, option) => option && setEditingTile({ 
                                            ...editingTile, 
                                            access_mode: option.key as 'all_users' | 'specific_users'
                                        })}
                                        options={[
                                            { key: 'all_users', text: '👥 All Users (No restrictions)' },
                                            { key: 'specific_users', text: '🔒 Specific Users/Groups' }
                                        ]}
                                    />
                                    
                                    {editingTile.access_mode === 'specific_users' && (
                                        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginLeft: 16 } }}>
                                            <TagInput
                                                label="Allowed Users (Email addresses)"
                                                value={editingTile.allowed_users || []}
                                                onChange={(users) => setEditingTile({ 
                                                    ...editingTile, 
                                                    allowed_users: users
                                                })}
                                                placeholder="user@company.com, another@company.com"
                                                description="Enter email addresses of users who can access this data source"
                                                maxTags={50}
                                            />
                                            
                                            <TagInput
                                                label="Allowed Groups (Entra Group IDs)"
                                                value={editingTile.allowed_groups || []}
                                                onChange={(groups) => setEditingTile({ 
                                                    ...editingTile, 
                                                    allowed_groups: groups
                                                })}
                                                placeholder="a1b2c3d4-e5f6-... (Group ID from Entra)"
                                                description="Enter Entra ID group IDs. Users in these groups will have access."
                                                maxTags={50}
                                            />
                                            
                                            {(!editingTile.allowed_users?.length && !editingTile.allowed_groups?.length) && (
                                                <MessageBar messageBarType={MessageBarType.warning}>
                                                    <Text variant="small">
                                                        ⚠️ No users or groups specified - only admins will have access to this data source.
                                                    </Text>
                                                </MessageBar>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>

                                <Stack tokens={{ childrenGap: 12 }}>
                                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Settings</Text>
                                    
                                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                                        <Stack horizontal verticalAlign="center">
                                            <Checkbox
                                                checked={editingTile.enabled}
                                                onChange={(_, checked) => setEditingTile({ ...editingTile, enabled: checked || false })}
                                                label="Enabled"
                                            />
                                            <FieldInfo 
                                                title="Enabled" 
                                                description="Turn this data source on or off. Disabled sources won't be used for answering queries."
                                            />
                                        </Stack>

                                        <Stack horizontal verticalAlign="center">
                                            <Checkbox
                                                checked={editingTile.source_privacy === 'public'}
                                                onChange={(_, checked) => setEditingTile({ ...editingTile, source_privacy: checked ? 'public' : 'private' })}
                                                label="🌐 Public (FAQ Cache)"
                                                styles={{
                                                    label: { color: editingTile.source_privacy === 'public' ? '#004085' : '#666' }
                                                }}
                                            />
                                            <FieldInfo 
                                                title="Public (FAQ Cache)" 
                                                description="PUBLIC: Q&A pairs from this source can be cached in the cross-organization FAQ for faster responses to similar questions. PRIVATE: Sensitive data (emails, files, calendar) - answers are never cached or shared."
                                            />
                                        </Stack>

                                        <Stack horizontal verticalAlign="center">
                                            <Checkbox
                                                checked={editingTile.use_smart_search}
                                                onChange={(_, checked) => setEditingTile({ ...editingTile, use_smart_search: checked || false })}
                                                label="Smart Search"
                                            />
                                            <FieldInfo 
                                                title="Smart Search" 
                                                description="ON: Uses AI-powered semantic search that understands meaning, synonyms, and context. Example: 'vacation policy' also finds 'PTO', 'time off', 'leave'. OFF: Keyword-only matching - requires exact word matches."
                                            />
                                        </Stack>

                                        <Stack horizontal verticalAlign="center">
                                            <Checkbox
                                                checked={editingTile.fallback_enabled}
                                                onChange={(_, checked) => setEditingTile({ ...editingTile, fallback_enabled: checked || false })}
                                                label="Fallback"
                                            />
                                            <FieldInfo 
                                                title="Fallback" 
                                                description="ON: If this source returns no results, the system will try other data sources. OFF: Only use this source - don't try alternatives. Useful for grounding sources where you want answers strictly from one knowledge base."
                                            />
                                        </Stack>
                                    </Stack>
                                </Stack>

                                {editingTile.connector_type === 'azure_search' && (
                                    <Stack tokens={{ childrenGap: 10 }}>
                                        <Checkbox
                                            checked={editingTile.enable_acl_filtering}
                                            onChange={(_, checked) => setEditingTile({ 
                                                ...editingTile, 
                                                enable_acl_filtering: checked || false
                                            })}
                                            label="🔒 Enable ACL Security Filtering"
                                            title="Enable user-based security filtering for this data source. Only documents the user has permission to access will be returned in search results."
                                            styles={{
                                                root: { marginTop: 10 },
                                                label: { fontWeight: FontWeights.semibold, color: '#667eea' }
                                            }}
                                        />
                                        
                                        <Checkbox
                                            checked={editingTile.is_grounding_source}
                                            onChange={(_, checked) => setEditingTile({ 
                                                ...editingTile, 
                                                is_grounding_source: checked || false,
                                                grounding_system_message: checked ? (editingTile.grounding_system_message || 'You must answer ONLY based on the provided documents. If the information is not found in the documents, respond with the no-results message.') : '',
                                                grounding_no_results_message: checked ? (editingTile.grounding_no_results_message || 'I don\'t have information about this topic in the available documents.') : ''
                                            })}
                                            label="🎯 Grounding Source"
                                            title="Use this Azure Search index as the exclusive grounding source. When enabled, all queries will be grounded to this index only, bypassing other data sources and real-time search."
                                            styles={{
                                                root: { marginTop: 10 },
                                                label: { fontWeight: FontWeights.semibold, color: '#8b5cf6' }
                                            }}
                                        />
                                        
                                        {editingTile.is_grounding_source && (
                                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { marginLeft: 20, padding: '10px', backgroundColor: '#f8f6ff', borderRadius: '4px', border: '1px solid #e1d5ff' } }}>
                                                <TextField
                                                    label="🎯 Grounding System Message"
                                                    value={editingTile.grounding_system_message || ''}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        grounding_system_message: value || ''
                                                    })}
                                                    multiline
                                                    rows={3}
                                                    placeholder="You must answer ONLY based on the provided documents. If the information is not found in the documents, respond with the no-results message."
                                                    description="Custom system message for strict grounding behavior"
                                                    styles={{
                                                        fieldGroup: { backgroundColor: 'white' }
                                                    }}
                                                />
                                                
                                                <TextField
                                                    label="🎯 No Results Message"
                                                    value={editingTile.grounding_no_results_message || ''}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        grounding_no_results_message: value || ''
                                                    })}
                                                    multiline
                                                    rows={2}
                                                    placeholder="I don't have information about this topic in the available documents."
                                                    description="Message shown when no relevant documents are found"
                                                    styles={{
                                                        fieldGroup: { backgroundColor: 'white' }
                                                    }}
                                                />
                                            </Stack>
                                        )}
                                        
                                        <Checkbox
                                            checked={editingTile.use_agentic_retrieval}
                                            onChange={(_, checked) => setEditingTile({ 
                                                ...editingTile, 
                                                use_agentic_retrieval: checked || false,
                                                agentic_knowledge_base: checked ? (editingTile.agentic_knowledge_base || '') : '',
                                                agentic_user_description: checked ? (editingTile.agentic_user_description || '') : '',
                                                agentic_output_mode: checked ? (editingTile.agentic_output_mode || 'markdown') : '',
                                                agentic_max_tokens: checked ? (editingTile.agentic_max_tokens || 4000) : 4000,
                                                agentic_temperature: checked ? (editingTile.agentic_temperature || 0.0) : 0.0
                                            })}
                                            label="🤖 Enable Agentic Retrieval"
                                            title="Use Azure AI Search Agentic Retrieval for intelligent document analysis with multi-step reasoning. Perfect for complex queries requiring cross-document synthesis."
                                            styles={{
                                                root: { marginTop: 10 },
                                                label: { fontWeight: FontWeights.semibold, color: '#16a085' }
                                            }}
                                        />
                                        
                                        {editingTile.use_agentic_retrieval && (
                                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { marginLeft: 20, padding: '10px', backgroundColor: '#f0fffe', borderRadius: '4px', border: '1px solid #b2dfdb' } }}>
                                                <TextField
                                                    label="🤖 Knowledge Base Name"
                                                    value={editingTile.agentic_knowledge_base || ''}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_knowledge_base: value || ''
                                                    })}
                                                    placeholder="banking-reports-kb"
                                                    description="Azure AI Search knowledge base name for agentic retrieval"
                                                    required
                                                    styles={{
                                                        fieldGroup: { backgroundColor: 'white' }
                                                    }}
                                                />
                                                
                                                <TextField
                                                    label="📝 Description & Context"
                                                    value={editingTile.agentic_user_description || ''}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_user_description: value || ''
                                                    })}
                                                    multiline
                                                    rows={3}
                                                    placeholder="Describe what this data source contains and how it should be used. For example: Banking and financial reports for risk analysis, regulatory compliance documents, quarterly earnings reports with detailed financial metrics..."
                                                    description="Natural description of your data source - AI will enhance this into professional instructions"
                                                    styles={{
                                                        fieldGroup: { backgroundColor: 'white' }
                                                    }}
                                                />
                                                
                                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                                    <DefaultButton
                                                        text="✨ Enhance Description"
                                                        onClick={async () => {
                                                            if (!editingTile.name?.trim()) {
                                                                setMessage({ text: 'Data source name is required', type: MessageBarType.error });
                                                                return;
                                                            }
                                                            
                                                            try {
                                                                setLoading(true);
                                                                const response = await authenticatedFetch('/api/admin/agentic-retrieval/enhance-description', {
                                                                    method: 'POST',
                                                                    headers: { 'Content-Type': 'application/json' },
                                                                    body: JSON.stringify({
                                                                        name: editingTile.name,
                                                                        description: editingTile.agentic_user_description || ''
                                                                    })
                                                                });
                                                                
                                                                const data = await response.json();
                                                                
                                                                if (data.success && data.enhanced_description) {
                                                                    setEditingTile({
                                                                        ...editingTile,
                                                                        agentic_user_description: data.enhanced_description
                                                                    });
                                                                    setMessage({ text: 'Description enhanced! Now click Enhance Instructions.', type: MessageBarType.success });
                                                                } else {
                                                                    setMessage({ text: data.error || 'Enhancement failed', type: MessageBarType.error });
                                                                }
                                                            } catch (error) {
                                                                console.error('Enhancement error:', error);
                                                                setMessage({ text: 'Enhancement failed', type: MessageBarType.error });
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading}
                                                        title="Enhance the description with AI to get better instruction generation results"
                                                    />
                                                    {loading && <Spinner size={SpinnerSize.small} />}
                                                </Stack>
                                                
                                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                                    <PrimaryButton
                                                        text="🤖 Enhance Instructions"
                                                        onClick={async () => {
                                                            if (!editingTile.agentic_user_description?.trim()) {
                                                                setMessage({ text: 'Please provide a description first', type: MessageBarType.error });
                                                                return;
                                                            }
                                                            
                                                            if (!editingTile.agentic_knowledge_base?.trim()) {
                                                                setMessage({ text: 'Please specify knowledge base name first', type: MessageBarType.error });
                                                                return;
                                                            }
                                                            
                                                            try {
                                                                setLoading(true);
                                                                const response = await authenticatedFetch('/api/admin/agentic-retrieval/enhance-instructions', {
                                                                    method: 'POST',
                                                                    headers: {
                                                                        'Content-Type': 'application/json'
                                                                    },
                                                                    body: JSON.stringify({
                                                                        user_description: editingTile.agentic_user_description,
                                                                        data_source_name: editingTile.name,
                                                                        knowledge_base: editingTile.agentic_knowledge_base
                                                                    })
                                                                });
                                                                
                                                                const data = await response.json();
                                                                
                                                                if (data.success && data.enhanced_instructions) {
                                                                    setEditingTile({
                                                                        ...editingTile,
                                                                        agentic_retrieval_instructions: data.enhanced_instructions.retrieval_instructions,
                                                                        agentic_answer_instructions: data.enhanced_instructions.answer_instructions
                                                                    });
                                                                    setMessage({ text: 'Instructions enhanced successfully!', type: MessageBarType.success });
                                                                } else {
                                                                    setMessage({ text: data.error || 'Enhancement failed', type: MessageBarType.error });
                                                                }
                                                            } catch (error) {
                                                                console.error('Enhancement error:', error);
                                                                setMessage({ text: 'Enhancement failed', type: MessageBarType.error });
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading || !editingTile.agentic_user_description?.trim() || !editingTile.agentic_knowledge_base?.trim()}
                                                    />
                                                    {loading && <Spinner size={SpinnerSize.small} />}
                                                </Stack>
                                                
                                                <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 10 } }}>
                                                    <Text variant="small" styles={{ root: { fontWeight: 'semibold', alignSelf: 'center' } }}>
                                                        Quick Templates:
                                                    </Text>
                                                    <DefaultButton
                                                        text="🏦 Banking Reports"
                                                        onClick={() => {
                                                            const bankingRetrievalInstructions = `נתח את הדוחות הבנקאיים בעברית ומצא מידע רלוונטי לשאלה. 
התמקד בנתונים כספיים מדויקים, אחוזים, וסכומים.
השווה בין בנקים שונים כשנדרש.
חפש במידע על: רווחים, הפסדים, סיכונים, ביקורת, והנהלה.`;
                                                            
                                                            const bankingAnswerInstructions = `ענה בעברית באופן מקצועי ומדויק.
השתמש במידע מהמקורות בלבד - אל תוסיף מידע שאינו קיים במסמכים.
כלול מספרים מדויקים ואחוזים מהדוחות.
אם משווה בין בנקים, הבהר את ההבדלים בבירור.
אסור לך להזכיר בנקים או חברות שאינם מופיעים במסמכים המצורפים.
אם אין מידע על בנק או נושא ספציפי, כתב במפורש "לא נמצא מידע במסמכים שהתקבלו".
אל תמציא או תשער נתונים כספיים.`;
                                                            
                                                            setEditingTile({
                                                                ...editingTile,
                                                                agentic_retrieval_instructions: bankingRetrievalInstructions,
                                                                agentic_answer_instructions: bankingAnswerInstructions
                                                            });
                                                        }}
                                                        styles={{ root: { minWidth: 'auto' } }}
                                                    />
                                                    <DefaultButton
                                                        text="📊 General Business"
                                                        onClick={() => {
                                                            const businessRetrievalInstructions = `Search for comprehensive business information relevant to the query.
Focus on factual data, metrics, financial information, and operational details.
Look for information about: performance, strategy, operations, and market analysis.`;
                                                            
                                                            const businessAnswerInstructions = `Provide professional and accurate answers based only on the source documents.
Include specific numbers, percentages, and factual data from the documents.
If comparing different entities, clearly highlight the differences.
Never mention companies or organizations not present in the source documents.
If information about a specific topic is unavailable, explicitly state "This information is not available in the provided documents".
Do not fabricate or speculate on business data.`;
                                                            
                                                            setEditingTile({
                                                                ...editingTile,
                                                                agentic_retrieval_instructions: businessRetrievalInstructions,
                                                                agentic_answer_instructions: businessAnswerInstructions
                                                            });
                                                        }}
                                                        styles={{ root: { minWidth: 'auto' } }}
                                                    />
                                                    <DefaultButton
                                                        text="🔬 Research Papers"
                                                        onClick={() => {
                                                            const researchRetrievalInstructions = `Search for academic and research information relevant to the query.
Focus on findings, methodologies, data, and conclusions.
Look for: research results, statistical data, experimental findings, and citations.`;
                                                            
                                                            const researchAnswerInstructions = `Provide scientifically accurate answers based only on the research documents.
Include specific findings, statistical data, and methodological details.
Cite sources properly using [1][2][3] format.
Never make claims not supported by the research documents.
If research data is unavailable, state "This information is not available in the provided research documents".
Maintain academic objectivity and avoid speculation.`;
                                                            
                                                            setEditingTile({
                                                                ...editingTile,
                                                                agentic_retrieval_instructions: researchRetrievalInstructions,
                                                                agentic_answer_instructions: researchAnswerInstructions
                                                            });
                                                        }}
                                                        styles={{ root: { minWidth: 'auto' } }}
                                                    />
                                                </Stack>
                                                
                                                <TextField
                                                    label="🔍 Retrieval Instructions (AI Enhanced)"
                                                    value={editingTile.agentic_retrieval_instructions || ''}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_retrieval_instructions: value || ''
                                                    })}
                                                    multiline
                                                    rows={4}
                                                    placeholder="Enhanced retrieval instructions will appear here after using AI enhancement..."
                                                    description={`Instructions for document retrieval (${editingTile.agentic_retrieval_instructions?.length || 0}/4096 chars)`}
                                                    styles={{
                                                        fieldGroup: { 
                                                            backgroundColor: 'white',
                                                            borderColor: (editingTile.agentic_retrieval_instructions?.length || 0) > 4096 ? '#d13438' : undefined
                                                        },
                                                        field: { fontFamily: 'Monaco, monospace', fontSize: '12px' }
                                                    }}
                                                />
                                                
                                                <TextField
                                                    label="💬 Answer Instructions (AI Enhanced)"
                                                    value={editingTile.agentic_answer_instructions || ''}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_answer_instructions: value || ''
                                                    })}
                                                    multiline
                                                    rows={4}
                                                    placeholder="Enhanced answer instructions will appear here after using AI enhancement..."
                                                    description={`Instructions for answer formatting (${editingTile.agentic_answer_instructions?.length || 0}/4096 chars)`}
                                                    styles={{
                                                        fieldGroup: { 
                                                            backgroundColor: 'white',
                                                            borderColor: (editingTile.agentic_answer_instructions?.length || 0) > 4096 ? '#d13438' : undefined
                                                        },
                                                        field: { fontFamily: 'Monaco, monospace', fontSize: '12px' }
                                                    }}
                                                />
                                                
                                                {/* Character limit warning */}
                                                {((editingTile.agentic_retrieval_instructions?.length || 0) > 4096 || 
                                                  (editingTile.agentic_answer_instructions?.length || 0) > 4096) && (
                                                    <MessageBar messageBarType={MessageBarType.warning}>
                                                        ⚠️ Azure limits instructions to 4096 characters each. Instructions will be truncated when synced.
                                                    </MessageBar>
                                                )}
                                                
                                                {/* Sync Instructions to Azure KB Button */}
                                                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                                                    <PrimaryButton
                                                        text={syncingInstructions ? "Syncing..." : "📤 Sync Instructions to Azure KB"}
                                                        onClick={syncInstructionsToKB}
                                                        disabled={syncingInstructions || !editingTile.agentic_knowledge_base || (!editingTile.agentic_retrieval_instructions && !editingTile.agentic_answer_instructions)}
                                                        iconProps={{ iconName: 'CloudUpload' }}
                                                        styles={{
                                                            root: { 
                                                                backgroundColor: '#16a085',
                                                                borderColor: '#16a085'
                                                            },
                                                            rootHovered: {
                                                                backgroundColor: '#1abc9c',
                                                                borderColor: '#1abc9c'
                                                            }
                                                        }}
                                                    />
                                                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                                        Updates instructions directly in Azure Knowledge Base
                                                    </Text>
                                                </Stack>
                                                
                                                <Dropdown
                                                    label="📋 Output Format"
                                                    selectedKey={editingTile.agentic_output_mode || 'markdown'}
                                                    onChange={(_, option) => option && setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_output_mode: option.key as string 
                                                    })}
                                                    options={[
                                                        { key: 'markdown', text: 'Markdown (Rich formatting)' },
                                                        { key: 'text', text: 'Plain Text' },
                                                        { key: 'citations', text: 'Text with Detailed Citations' }
                                                    ]}
                                                    styles={{
                                                        dropdown: { backgroundColor: 'white' }
                                                    }}
                                                />
                                                
                                                <Dropdown
                                                    label="🧠 Reasoning Effort (Performance)"
                                                    selectedKey={editingTile.agentic_reasoning_effort || 'low'}
                                                    onChange={(_, option) => option && setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_reasoning_effort: option.key as string
                                                    })}
                                                    options={[
                                                        { key: 'minimal', text: 'Minimal (Fastest - basic retrieval)' },
                                                        { key: 'low', text: 'Low (Fast - ~16s)' },
                                                        { key: 'medium', text: 'Medium (Thorough - ~21s)' }
                                                    ]}
                                                    styles={{
                                                        dropdown: { backgroundColor: 'white' }
                                                    }}
                                                />
                                                
                                                <SpinButton
                                                    label="📏 Max Output Size (tokens)"
                                                    value={String(editingTile.agentic_max_output_size || 5000)}
                                                    min={1000}
                                                    max={10000}
                                                    step={500}
                                                    onChange={(_, value) => setEditingTile({ 
                                                        ...editingTile, 
                                                        agentic_max_output_size: parseInt(value || '5000', 10)
                                                    })}
                                                    styles={{
                                                        spinButtonWrapper: { backgroundColor: 'white' }
                                                    }}
                                                />
                                            </Stack>
                                        )}
                                    </Stack>
                                )}

                                <TagInput
                                    label="Examples"
                                    value={editingTile.examples}
                                    onChange={(examples) => setEditingTile({ 
                                        ...editingTile, 
                                        examples: examples
                                    })}
                                    placeholder="Enter example queries separated by commas: מה הרווח השנתי?, What is the annual profit?, כמה שעות עבד המבקר?"
                                    description="Example queries that would use this data source. You can enter multiple examples separated by commas, or press Enter to add individual examples."
                                    maxTags={20}
                                />

                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                    <PrimaryButton 
                                        text={isSaving ? "Saving..." : "Save"}
                                        onClick={saveTile}
                                        disabled={isSaving || !editingTile.name || (editingTile.connector_type === 'azure_search' && editingTile.azure_indexes.length === 0)}
                                    />
                                    {isSaving && <Spinner size={SpinnerSize.small} label="Saving..." />}
                                    <DefaultButton 
                                        text="Cancel" 
                                        onClick={closeEditPanel}
                                        disabled={isSaving}
                                    />
                                </Stack>
                            </Stack>
                        </div>
                    )}
                </Panel>

                {/* Upload Panel for Azure Blob tiles */}
                <Panel
                    isOpen={showUploadPanel}
                    onDismiss={() => {
                        setShowUploadPanel(false);
                        setUploadingTile(null);
                        setContainerFiles([]);
                    }}
                    headerText={`📤 Upload Files - ${uploadingTile?.name || ''}`}
                    type={PanelType.medium}
                >
                    {uploadingTile && (
                        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: '20px 0' } }}>
                            <Stack tokens={{ childrenGap: 8 }}>
                                <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold } }}>
                                    Container: <span style={{ color: '#667eea' }}>{uploadingTile.blob_container}</span>
                                </Text>
                                {uploadingTile.agentic_knowledge_base && (
                                    <Text variant="small" styles={{ root: { color: '#16a085' } }}>
                                        🤖 Knowledge Base: {uploadingTile.agentic_knowledge_base}
                                    </Text>
                                )}
                            </Stack>

                            <Separator />

                            {/* Upload Section */}
                            <Stack tokens={{ childrenGap: 10 }}>
                                <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold } }}>
                                    Upload New Files
                                </Text>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.ppt,.pptx,.txt,.md,.json,.csv"
                                />
                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                    <PrimaryButton
                                        iconProps={{ iconName: 'Upload' }}
                                        text={uploadingFiles ? 'Uploading...' : 'Select Files'}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingFiles}
                                    />
                                    <DefaultButton
                                        iconProps={{ iconName: 'Sync' }}
                                        text="Refresh List"
                                        onClick={() => uploadingTile.blob_container && loadContainerFiles(uploadingTile.blob_container)}
                                        disabled={loadingFiles}
                                    />
                                </Stack>
                                <Text variant="small" styles={{ root: { color: '#666' } }}>
                                    Supported: PDF, Word, Excel, PowerPoint, Text, Markdown, JSON, CSV
                                </Text>
                            </Stack>

                            <Separator />

                            {/* Files List */}
                            <Stack tokens={{ childrenGap: 10 }}>
                                <Text variant="mediumPlus" styles={{ root: { fontWeight: FontWeights.semibold } }}>
                                    Files in Container ({containerFiles.length})
                                </Text>
                                {loadingFiles ? (
                                    <Text>Loading files...</Text>
                                ) : containerFiles.length === 0 ? (
                                    <MessageBar messageBarType={MessageBarType.info}>
                                        No files in this container yet. Upload some documents to get started.
                                    </MessageBar>
                                ) : (
                                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #edebe9', borderRadius: '4px' }}>
                                        {containerFiles.map((file, idx) => (
                                            <Stack 
                                                key={idx} 
                                                horizontal 
                                                verticalAlign="center" 
                                                tokens={{ childrenGap: 10 }}
                                                styles={{ 
                                                    root: { 
                                                        padding: '8px 12px', 
                                                        borderBottom: idx < containerFiles.length - 1 ? '1px solid #edebe9' : 'none',
                                                        backgroundColor: idx % 2 === 0 ? '#faf9f8' : 'white'
                                                    } 
                                                }}
                                            >
                                                <Icon iconName="Document" styles={{ root: { color: '#667eea' } }} />
                                                <Stack style={{ flex: 1 }}>
                                                    <Text variant="small" styles={{ root: { fontWeight: FontWeights.semibold } }}>
                                                        {file.name}
                                                    </Text>
                                                    {file.size && (
                                                        <Text variant="xSmall" styles={{ root: { color: '#666' } }}>
                                                            {(file.size / 1024).toFixed(1)} KB
                                                        </Text>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        ))}
                                    </div>
                                )}
                            </Stack>

                            <Separator />

                            {/* Indexer Info */}
                            <MessageBar messageBarType={MessageBarType.info}>
                                <Text variant="small">
                                    📝 After uploading files, the Azure AI Search indexer will automatically process new documents. 
                                    This typically takes a few minutes depending on file size and count.
                                </Text>
                            </MessageBar>

                            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
                                <DefaultButton 
                                    text="Close" 
                                    onClick={() => {
                                        setShowUploadPanel(false);
                                        setUploadingTile(null);
                                        setContainerFiles([]);
                                    }}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Panel>
            </Stack>
        </div>
    );
};

export default DataSourceTiles;